import { rpx } from 'client/lib/rpx-client';
import { useMemo } from 'preact/hooks';
import { indexBy } from 'shared/utils';
import { RouteLoadProps } from '@components/router';
import { CALENDAR_PRODUCT_ID } from 'shared/ids';

export const store = rpx.superadminPricing;

export const priceStore = rpx.prices;

export const couponStore = rpx.coupons;

export const defaultFolderId = 'fold-coreruzuku';

function getDefaultFolderId(productId: string): string {
  if (productId === CALENDAR_PRODUCT_ID) {
    return 'fold-calendar';
  }
  return defaultFolderId;
}

export type LoadResult = Awaited<ReturnType<typeof store.init>>;

export type Price = LoadResult['prices'][0] & { type: 'price' };

export type Coupon = LoadResult['coupons'][0] & { type: 'coupon' };

export type Folder = {
  type: 'folder';
  id: string;
  name: string;
};

export type Item = Folder | Price | Coupon;

export interface FolderStats {
  numItems: number;
  numSignups: number;
}

export function useFolderStats(state: State) {
  return useMemo(() => {
    const stats: Record<string, FolderStats> = {};
    Object.entries(state.itemToFolder).forEach(([itemId, folderId]) => {
      const item = state.items[itemId];
      if (!item) {
        return;
      }
      const fstat: FolderStats = stats[folderId] || { numSignups: 0, numItems: 0 };
      stats[folderId] = fstat;
      ++fstat.numItems;
      if (item.type === 'price') {
        fstat.numSignups += item.numSignups || 0;
      }
    });
    return stats;
  }, [state.itemToFolder]);
}

export async function load(route: RouteLoadProps) {
  const result = await store.init({
    productId: route.params.productId,
  });
  const items: Record<string, Item> = indexBy(
    (x) => x.id,
    [
      ...result.prices.map<Price>((x) => ({ ...x, type: 'price' })),
      ...result.coupons.map<Coupon>((x) => ({ ...x, type: 'coupon' })),
      ...result.folders.map<Folder>((x) => ({ ...x, type: 'folder' })),
    ],
  );

  const itemToFolder: Record<string, string> = {};
  const productDefaultFolder = getDefaultFolderId(route.params.productId);

  result.prices.forEach((x) => {
    itemToFolder[x.id] = productDefaultFolder;
  });

  result.coupons.forEach((x) => {
    itemToFolder[x.id] = productDefaultFolder;
  });

  result.folderItems.forEach((x) => {
    itemToFolder[x.itemId] = x.folderId;
  });

  const selected: string[] = [];

  return {
    itemToFolder,
    items,
    selected,
    priceCoupons: Array.from(result.priceCoupons),
    product: result.product,
  };
}

export type State = Awaited<ReturnType<typeof load>>;

export type SetState = (fn: (state: State) => void) => void;
